import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import * as api from 'api/api'
import { Member, Project, ProjectStatus, ReportListItem } from 'api/types'
import { useAuth } from 'auth/AuthContext'
import { Icon } from 'components/Icon'
import { Avatar } from 'components/app-shell/atoms/Avatar'
import { AvatarStack, EmptyState, ErrorState, IconButton, PlanBar, ProgressBar, ProgressRing, Spinner, StatusBadge } from 'components/ui'
import { PROJECT_STATUS_LABELS, ROLE_LABELS, addDays, daysBetween, formatDate } from 'lib/format'
import { useAsync } from 'lib/hooks'
import { t } from 'lib/i18n'

type View = 'cards' | 'table'

interface SiteStats {
  actual: number | null
  planned: number
  last?: ReportListItem
  pmNames: string[]
  engineerNames: string[]
}

const planFraction = (project: Project, today: string) => {
  const total = Math.max(1, daysBetween(project.startDate, project.endDate) + 1)
  const clampedToday = today < project.startDate ? project.startDate : today > project.endDate ? project.endDate : today
  const elapsed = Math.max(0, daysBetween(project.startDate, clampedToday) + 1)
  return Math.min(1, elapsed / total)
}

const planStatus = (actual: number, planned: number) => {
  const delta = Math.round((actual - planned) * 100)
  if (delta > 2) return { label: t(`متقدم ${delta}% عن الخطة`, `${delta}% ahead of plan`), tone: 'ahead' as const }
  if (delta < -2) return { label: t(`متأخر ${Math.abs(delta)}% عن الخطة`, `${Math.abs(delta)}% behind plan`), tone: 'behind' as const }
  return { label: t('وفق الخطة', 'On plan'), tone: 'onplan' as const }
}

const computeStats = async (project: Project, today: string): Promise<SiteStats> => {
  const clampedToday = today > project.endDate ? project.endDate : today
  const [summary, recent, members] = await Promise.all([
    api.fetchSummary(project.id, project.startDate, clampedToday),
    api.fetchReports(project.id, addDays(today, -13), today),
    api.fetchMembers(project.id).catch(() => [] as Member[]),
  ])
  const counts = new Map(summary.statusCounts.map((item) => [item.status, item.count]))
  const complete = (counts.get('COMPLETE') ?? 0) + (counts.get('CREATED') ?? 0)
  const missing = counts.get('MISSING') ?? 0
  const due = summary.workingDays + missing
  const last = recent.filter((item) => item.workflow !== 'EMPTY').sort((a, b) => (a.reportDate < b.reportDate ? 1 : -1))[0]
  const pmNames = members.filter((member) => member.role === 'PROJECT_MANAGER').map((member) => member.user.name)
  const engineerNames = members.filter((member) => member.role === 'SITE_ENGINEER').map((member) => member.user.name)
  return { actual: due ? complete / due : null, engineerNames, last, pmNames, planned: planFraction(project, today) }
}

const SiteCard = ({ project, stats }: { project: Project; stats?: SiteStats }) => {
  const actual = stats?.actual
  const plan = stats ? planStatus(actual ?? 0, stats.planned) : undefined

  return (
    <li>
      <Link to={`/projects/${project.id}/overview`} className="project-card">
        <div className="project-card__top">
          <span className="chip">{ROLE_LABELS[project.myRole]}</span>
          <span className={`badge badge--project-${project.status.toLowerCase()}`}>{PROJECT_STATUS_LABELS[project.status]}</span>
        </div>
        <h2>{project.name}</h2>
        {project.location ? (
          <p className="muted small">
            <Icon name="mapPin" size={14} /> {project.location}
          </p>
        ) : null}

        {stats && actual != null ? (
          <>
            <div className="site-card__head">
              <ProgressRing value={actual} size={60} strokeWidth={6} />
              <div className="site-card__plan">
                <div className="site-card__plan-head">
                  <span>{t('فعلي مقابل مخطط', 'Actual vs planned')}</span>
                  {plan ? <span className={`small plan-${plan.tone}`}>{plan.label}</span> : null}
                </div>
                <PlanBar
                  actual={actual}
                  planned={stats.planned}
                  label={t(`فعلي ${Math.round(actual * 100)}% مقابل مخطط ${Math.round(stats.planned * 100)}%`, `Actual ${Math.round(actual * 100)}% vs planned ${Math.round(stats.planned * 100)}%`)}
                />
                <div className="legend">
                  <span className="legend__item">
                    <span className="legend__dot" /> {t(`فعلي ${Math.round(actual * 100)}%`, `Actual ${Math.round(actual * 100)}%`)}
                  </span>
                  <span className="legend__item">
                    <span className="legend__dot legend__dot--planned" /> {t(`مخطط ${Math.round(stats.planned * 100)}%`, `Planned ${Math.round(stats.planned * 100)}%`)}
                  </span>
                </div>
              </div>
            </div>
            <hr className="divider" />
            <div className="site-card__last">
              {stats.last ? (
                <span className="muted">{t(`آخر تقرير · ${formatDate(stats.last.reportDate)}`, `Last report · ${formatDate(stats.last.reportDate)}`)}</span>
              ) : (
                <span className="muted">{t('لا توجد تقارير حديثة', 'No recent reports')}</span>
              )}
              {stats.last ? <StatusBadge status={stats.last.status} /> : null}
            </div>
            {stats.last?.preparedByName ? (
              <div className="site-card__prep">
                <Avatar initial={stats.last.preparedByName.trim().slice(0, 1)} />
                <div className="site-card__prep-name">
                  <strong>{stats.last.preparedByName}</strong>
                  <small>{ROLE_LABELS.SITE_ENGINEER}</small>
                </div>
              </div>
            ) : null}
          </>
        ) : (
          <p className="muted small">{t('لا تتوفر بيانات التقدم بعد', 'Progress data is not available yet')}</p>
        )}
      </Link>
    </li>
  )
}

const SitesTable = ({
  projects,
  allCount,
  statsByProject,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
}: {
  projects: Project[]
  allCount: number
  statsByProject: Map<string, SiteStats>
  page: number
  pageSize: number
  onPageChange: (page: number) => void
  onPageSizeChange: (size: number) => void
}) => {
  const pageCount = Math.max(1, Math.ceil(projects.length / pageSize))
  const safePage = Math.min(page, pageCount)
  const pageStart = projects.length ? (safePage - 1) * pageSize + 1 : 0
  const pageEnd = Math.min(safePage * pageSize, projects.length)
  const pageItems = projects.slice((safePage - 1) * pageSize, safePage * pageSize)

  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th scope="col">{t('الموقع', 'Site')}</th>
            <th scope="col">{t('المكان', 'Location')}</th>
            <th scope="col">{ROLE_LABELS.PROJECT_MANAGER}</th>
            <th scope="col">{t('المهندسون', 'Engineers')}</th>
            <th scope="col">{t('التقدم', 'Progress')}</th>
            <th scope="col">{t('الجدول', 'Schedule')}</th>
            <th scope="col">{t('الحالة', 'Status')}</th>
            <th scope="col">{t('آخر تقرير', 'Last report')}</th>
          </tr>
        </thead>
        <tbody>
          {pageItems.map((project) => {
            const stats = statsByProject.get(project.id)
            const actual = stats?.actual
            const plan = stats && actual != null ? planStatus(actual, stats.planned) : undefined
            const tone = plan?.tone === 'behind' ? (actual! < stats!.planned - 0.1 ? 'danger' : 'warn') : 'good'
            return (
              <tr key={project.id}>
                <td>
                  <Link to={`/projects/${project.id}/overview`} className="code-link">
                    {project.name}
                  </Link>
                </td>
                <td>{project.location || '—'}</td>
                <td>{stats?.pmNames.length ? <AvatarStack names={stats.pmNames} /> : <span className="muted">—</span>}</td>
                <td>{stats?.engineerNames.length ? <AvatarStack names={stats.engineerNames} /> : <span className="muted">—</span>}</td>
                <td>
                  {actual != null ? (
                    <div className="fill">
                      <ProgressBar value={actual} label={t(`التقدم — ${project.name}`, `Progress — ${project.name}`)} tone={tone} />
                      <span className="num">{Math.round(actual * 100)}%</span>
                    </div>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
                <td>{plan ? <span className={`small plan-${plan.tone}`}>{plan.label}</span> : <span className="muted">—</span>}</td>
                <td>
                  <span className={`badge badge--project-${project.status.toLowerCase()}`}>{PROJECT_STATUS_LABELS[project.status]}</span>
                </td>
                <td>{stats?.last ? formatDate(stats.last.reportDate) : <span className="muted">—</span>}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
      <div className="table-footer">
        <span className="muted small">
          {allCount !== projects.length ? t(`${projects.length} من ${allCount}`, `${projects.length} of ${allCount}`) : t(`${projects.length} موقعاً`, `${projects.length} sites`)}
        </span>
        <span className="spacer" />
        <label className="table-footer__rows">
          {t('صفوف لكل صفحة', 'Rows per page')}
          <select className="input" value={pageSize} onChange={(event) => onPageSizeChange(Number(event.target.value))}>
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
        </label>
        <span className="muted small">
          {pageStart}–{pageEnd} {t('من', 'of')} {projects.length}
        </span>
        <div className="table-footer__pagination">
          <IconButton icon="chevronsRight" label={t('الصفحة الأولى', 'First page')} disabled={safePage <= 1} onClick={() => onPageChange(1)} />
          <IconButton
            icon="chevronRight"
            label={t('الصفحة السابقة', 'Previous page')}
            disabled={safePage <= 1}
            onClick={() => onPageChange(Math.max(1, safePage - 1))}
          />
          <IconButton
            icon="chevronLeft"
            label={t('الصفحة التالية', 'Next page')}
            disabled={safePage >= pageCount}
            onClick={() => onPageChange(Math.min(pageCount, safePage + 1))}
          />
          <IconButton icon="chevronsLeft" label={t('الصفحة الأخيرة', 'Last page')} disabled={safePage >= pageCount} onClick={() => onPageChange(pageCount)} />
        </div>
      </div>
    </div>
  )
}

const STATUS_OPTIONS: ProjectStatus[] = ['ACTIVE', 'ON_HOLD', 'COMPLETED']

/** Admin/PM "operations" view across every project (Figma: Admin / 01 · Sites — cards, 02 · Sites — table). */
export const SitesPage = () => {
  const { user } = useAuth()
  const [params, setParams] = useSearchParams()
  const view: View = params.get('view') === 'table' ? 'table' : 'cards'
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<Set<ProjectStatus>>(new Set())
  const [pmFilter, setPmFilter] = useState<Set<string>>(new Set())
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  const { data: projects, error, loading, reload } = useAsync(api.fetchProjects, [])
  const { data: today } = useAsync(api.fetchToday, [])

  const { data: statsByProject, loading: statsLoading } = useAsync(async () => {
    if (!projects?.length || !today) return new Map<string, SiteStats>()
    const entries = await Promise.all(projects.map(async (project): Promise<[string, SiteStats]> => [project.id, await computeStats(project, today)]))
    return new Map(entries)
  }, [projects, today])

  const pmOptions = useMemo(
    () => Array.from(new Set(Array.from(statsByProject?.values() ?? []).flatMap((stats) => stats.pmNames))).sort(),
    [statsByProject]
  )

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase()
    return (projects ?? []).filter((project) => {
      const stats = statsByProject?.get(project.id)
      if (statusFilter.size && !statusFilter.has(project.status)) return false
      if (pmFilter.size && !stats?.pmNames.some((name) => pmFilter.has(name))) return false
      if (term && !project.name.toLowerCase().includes(term) && !(project.location ?? '').toLowerCase().includes(term)) return false
      return true
    })
  }, [projects, statsByProject, search, statusFilter, pmFilter])

  const activeCount = projects?.filter((project) => project.status === 'ACTIVE').length ?? 0
  const onHoldCount = projects?.filter((project) => project.status === 'ON_HOLD').length ?? 0

  useEffect(() => {
    setPage(1)
  }, [search, statusFilter, pmFilter, pageSize])

  const setView = (next: View) => setParams(next === 'cards' ? {} : { view: next }, { replace: true })

  const toggleStatus = (status: ProjectStatus) =>
    setStatusFilter((current) => {
      const next = new Set(current)
      if (next.has(status)) next.delete(status)
      else next.add(status)
      return next
    })

  const togglePm = (name: string) =>
    setPmFilter((current) => {
      const next = new Set(current)
      if (next.has(name)) next.delete(name)
      else next.add(name)
      return next
    })

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1>{t('المواقع', 'Sites')}</h1>
          <p className="muted">
            {projects ? t(`${projects.length} موقعاً · ${activeCount} نشط، ${onHoldCount} متوقف`, `${projects.length} sites · ${activeCount} Active, ${onHoldCount} On hold`) : ''}
          </p>
        </div>
        <div className="toolbar">
          {user?.isAdmin ? (
            <Link to="/projects/new" className="btn btn--primary">
              <Icon name="plus" size={14} /> {t('إضافة موقع', 'Add site')}
            </Link>
          ) : null}
          <div className="segmented" role="group" aria-label={t('طريقة العرض', 'View')}>
            <button type="button" aria-pressed={view === 'cards'} onClick={() => setView('cards')}>
              <Icon name="grid" size={14} /> {t('بطاقات', 'Cards')}
            </button>
            <button type="button" aria-pressed={view === 'table'} onClick={() => setView('table')}>
              <Icon name="list" size={14} /> {t('جدول', 'Table')}
            </button>
          </div>
        </div>
      </div>

      <div className="toolbar">
        <div className="search">
          <Icon name="search" size={16} />
          <label htmlFor="sites-search" className="sr-only">
            {t('بحث', 'Search')}
          </label>
          <input
            id="sites-search"
            type="search"
            placeholder={t('بحث عن موقع أو مشروع...', 'Search for a site or project...')}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        <details className="dropdown">
          <summary className="btn">{t('الحالة', 'Status')}{statusFilter.size ? ` (${statusFilter.size})` : ''}</summary>
          <div className="dropdown__panel">
            {STATUS_OPTIONS.map((status) => (
              <label key={status} className="dropdown__item">
                <input type="checkbox" checked={statusFilter.has(status)} onChange={() => toggleStatus(status)} />
                {PROJECT_STATUS_LABELS[status]}
              </label>
            ))}
          </div>
        </details>
        {pmOptions.length ? (
          <details className="dropdown">
            <summary className="btn">{ROLE_LABELS.PROJECT_MANAGER}{pmFilter.size ? ` (${pmFilter.size})` : ''}</summary>
            <div className="dropdown__panel">
              {pmOptions.map((name) => (
                <label key={name} className="dropdown__item">
                  <input type="checkbox" checked={pmFilter.has(name)} onChange={() => togglePm(name)} />
                  {name}
                </label>
              ))}
            </div>
          </details>
        ) : null}
      </div>

      {loading && !projects ? (
        <Spinner />
      ) : error ? (
        <ErrorState message={error} onRetry={reload} />
      ) : !projects?.length ? (
        <EmptyState title={t('لا توجد مواقع مرتبطة بحسابك', 'No sites are linked to your account')} icon="mapPin" />
      ) : !visible.length ? (
        <EmptyState title={t('لا توجد نتائج مطابقة', 'No matching results')} icon="search" />
      ) : view === 'table' ? (
        <SitesTable
          projects={visible}
          allCount={projects.length}
          statsByProject={statsByProject ?? new Map()}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      ) : (
        <ul className="project-grid">
          {visible.map((project) => (
            <SiteCard key={project.id} project={project} stats={statsLoading ? undefined : statsByProject?.get(project.id)} />
          ))}
        </ul>
      )}
    </div>
  )
}
